import React from 'react';
import { cx } from './cx';

export interface TooltipProps {
  /** The visible label (translated); usually the control's own accessible name. */
  label: string;
  /** Where the label shows, relative to the control. */
  side?: 'left' | 'right' | 'top' | 'bottom';
  className?: string;
  /** One control (an IconButton). */
  children: React.ReactNode;
}

const SIDES = {
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  right: 'left-full top-1/2 ml-2 -translate-y-1/2',
  top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-2 -translate-x-1/2',
} as const;

/**
 * Hover / keyboard-focus label for an icon-only control. It is the visual copy of the control's
 * accessible name (IconButton `label`), so it is hidden from screen readers; never put
 * information here that the control does not already say. Touch screens have no hover: put the
 * word next to the icon there instead.
 */
export default function Tooltip({ label, side = 'top', className, children }: TooltipProps) {
  return (
    <span className={cx('group/tooltip relative inline-flex', className)}>
      {children}
      <span
        aria-hidden="true"
        data-tooltip=""
        className={cx(
          'pointer-events-none invisible absolute z-10 whitespace-nowrap rounded-control border k-panel bg-raised px-2.5 py-1.5 font-ui text-small text-primary opacity-0 transition-opacity duration-(--k-dur-fast)',
          'group-hover/tooltip:visible group-hover/tooltip:opacity-100',
          'group-has-focus-visible/tooltip:visible group-has-focus-visible/tooltip:opacity-100',
          SIDES[side]
        )}
      >
        {label}
      </span>
    </span>
  );
}
